// src/utils/act/actBreakdown.js
//
// Pure computation of ACT per-section, per-category performance from a question
// index + the student's answers. Categories are derived from the question
// metadata (ACT has no fixed subcategory tree), so this handles whatever
// categories appear in the test.

import { splitCorrect, pct, rxHoursFor, ACT_RX_MULT } from "./actStructure";

/**
 * @param {Map} questionIndex  qid -> { subject, category, correctAnswer }
 * @param {Object} answers     qid -> studentAnswer
 * @param {boolean} scienceSkipped
 * @returns {{ sections: { [section]: Array<{subject, category, total, correct, pct, rxHours, multiplier}> } }}
 */
export function computeActBreakdown(questionIndex, answers, scienceSkipped = false) {
  const map = {}; // section -> category -> { total, correct }

  for (const [qid, metaRaw] of questionIndex.entries()) {
    const meta = metaRaw || {};
    const subject = meta.subject;
    if (!subject) continue;
    if (scienceSkipped && subject === "Science") continue;

    const category = String(meta.category || "Uncategorized").trim() || "Uncategorized";
    if (category.toLowerCase() === "not scored") continue;

    const studentAns = String((answers && answers[qid]) || "").trim().toUpperCase();
    const correctArr = splitCorrect(meta.correctAnswer);
    const ok = !!studentAns && correctArr.includes(studentAns);

    if (!map[subject]) map[subject] = {};
    if (!map[subject][category]) map[subject][category] = { total: 0, correct: 0 };
    map[subject][category].total += 1;
    if (ok) map[subject][category].correct += 1;
  }

  const sections = {};
  Object.entries(map).forEach(([subject, cats]) => {
    sections[subject] = Object.entries(cats).map(([category, d]) => {
      const p = pct(d.correct, d.total);
      return {
        subject,
        category,
        total: d.total,
        correct: d.correct,
        pct: p,
        rxHours: rxHoursFor(subject, p),
        multiplier: ACT_RX_MULT[subject] ?? 2,
      };
    });
  });

  return { sections };
}
